// Màu thương hiệu: giữ đúng mã màu cho mảng màu, đồ thị, logo; tự suy ra sắc độ đậm/nhạt
// cho chữ và nút để luôn đạt tương phản WCAG AA ở nền sáng lẫn nền tối.
// Màu chủ đạo ⇒ dữ liệu, liên kết, tiêu điểm; màu thứ hai ⇒ nút chính và cột thương hiệu màn đăng nhập ở giao diện tối.
type RGB = [number, number, number];

function hexRgb(h: string | null | undefined, fallback: RGB = [47, 137, 201]): RGB {
  const m = String(h || '')
    .replace('#', '')
    .match(/^([0-9a-f]{6})$/i);
  if (!m) return fallback;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const lum = ([r, g, b]: RGB) => {
  const f = (c: number) => ((c /= 255) <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
export const contrast = (a: RGB, b: RGB) => {
  const x = lum(a);
  const y = lum(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};
const mix = (a: RGB, b: RGB, t: number): RGB => a.map((v, i) => Math.round(v * (1 - t) + b[i]! * t)) as RGB;
const hex = (rgb: RGB) => '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('');
const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];
const SURFACE_DARK: RGB = [34, 34, 36];

function until(c: RGB, toward: RGB, bg: RGB, min: number) {
  let out = c;
  for (let t = 0; contrast(out, bg) < min && t <= 1.0001; t += 0.025) out = mix(c, toward, t);
  return out;
}

export const DEFAULT_BRAND = '#2F89C9';
export const DEFAULT_BRAND2 = '#414042';

export function brandTokens(brand: string, brand2: string): Record<string, string> {
  const c = hexRgb(brand);
  // Màu thứ hai làm đậm tới ≥ 9,5:1 với chữ trắng: nút chính đọc như mực, cột thương hiệu đủ tối cho chữ trắng
  const deep = until(hexRgb(brand2, [65, 64, 66]), BLACK, WHITE, 9.5);
  const solid = until(c, BLACK, WHITE, 4.6);
  const softLight = mix(c, WHITE, 0.84);
  const softDark = mix(c, SURFACE_DARK, 0.84);
  return {
    '--brand': hex(c),
    '--brand-solid': hex(solid),
    '--brand-ink-l': hex(until(c, BLACK, softLight, 4.6)),
    '--brand-ink-d': hex(until(c, WHITE, softDark, 4.6)),
    '--on-brand': '#ffffff',
    '--primary-l': hex(deep),
    '--gate-bg-d': hex(mix(deep, [24, 24, 26], 0.55)),
  };
}

export function applyBrand(brand: string = DEFAULT_BRAND, brand2: string = DEFAULT_BRAND2) {
  const root = document.documentElement.style;
  for (const [k, v] of Object.entries(brandTokens(brand, brand2))) root.setProperty(k, v);
}

/** [màu chủ đạo, màu nút chính, tên] */
export const BRAND_PRESETS: [string, string, string][] = [
  ['#2F89C9', '#414042', 'Xanh #2F89C9 · than chì #414042'],
  ['#1F4FA3', '#1E2433', 'Xanh đậm · đêm'],
  ['#0B2E59', '#14202F', 'Xanh navy'],
  ['#9B1C2E', '#2B2526', 'Đỏ đô'],
  ['#0E7C66', '#22302C', 'Xanh ngọc'],
  ['#C2561A', '#2F2925', 'Cam đất'],
  ['#4B2E83', '#26222F', 'Tím than'],
  ['#9A7417', '#2E2A22', 'Vàng đồng'],
];
